import { useMemo } from "react";
import { fmtMoney } from "../../lib/format";
import {
  TARGET_MARGIN_THRESHOLD,
  TARGET_REVENUE_MULTIPLIER,
} from "../../lib/constants";
import { projectProfitability, marginColor } from "../../lib/profitability";
import InfoTip from "../InfoTip";
import { AlertTriangle } from "lucide-react";

// Profitability calculator — sticky bar at the bottom of the project form.
// Live on every keystroke: can this project still afford its team?
// Maths shared with the admin Projects table via lib/profitability.js.

const PLAIN = "var(--ink)";

export function ProfitabilityBar({ scopes, allocations, people, rates, sticky = true }) {
  const calc = useMemo(() => {
    const p = projectProfitability(
      { scopes, allocations },
      rates,
      people
    );
    const firmRate = rates?.firm?.count ? rates.firm.avgMonthly : null;
    let rpmColor = PLAIN;
    if (p.revPerPm != null && firmRate) {
      const x = p.revPerPm / firmRate;
      rpmColor = x < 1 ? "#6B2F2A" : x < TARGET_REVENUE_MULTIPLIER ? "#8a6d1a" : "#2C4A38";
    }
    return { ...p, firmRate, rpmColor };
  }, [scopes, allocations, people, rates]);

  const mColor = marginColor(calc.marginPct);
  const unprofitable = calc.marginPct != null && calc.marginPct < 0;
  const marginLabel =
    calc.marginPct == null ? undefined
    : calc.marginPct < 0 ? "loss-making"
    : calc.marginPct < TARGET_MARGIN_THRESHOLD ? `below ${Math.round(TARGET_MARGIN_THRESHOLD * 100)}% target`
    : "on target";

  return (
    <div
      className={`border border-[var(--rule-strong)] bg-[#F4F3EF] ${sticky ? "sticky bottom-0 z-30 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]" : ""}`}
      data-testid="profitability-bar"
    >
      <div className="px-5 pt-3 pb-1 flex items-center gap-2">
        <span className="eyebrow">Profitability</span>
        <InfoTip side="top" label="How this is calculated">
          <strong>Net fee</strong> = scope contract values (scheduled monthly fees where no contract value is set) MINUS sub-consultant costs — what the firm actually keeps. <strong>Personnel cost</strong> = sum of merged team rows (named + TBD) × tier-average monthly rate. Margin targets: green ≥ {Math.round(TARGET_MARGIN_THRESHOLD * 100)}%, red when cost exceeds fee. Revenue per person-month is measured against the firm-average monthly rate (green ≥ {TARGET_REVENUE_MULTIPLIER}×).
        </InfoTip>
        {unprofitable && (
          <span
            className="inline-flex items-center gap-1.5 text-[11px] font-mono-num text-[#6B2F2A]"
            title="Personnel cost exceeds fee — this project is not currently profitable at tier-average rates."
            data-testid="prof-warning"
          >
            <AlertTriangle size={12} /> not profitable at tier-average rates
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-y-3 px-5 pb-3 items-end">
        <Metric label="Net fee" value={fmtMoney(calc.fee)} sub={calc.subs ? `gross ${fmtMoney(calc.grossFee, { compact: true })} less subs ${fmtMoney(calc.subs, { compact: true })}` : undefined} testid="prof-fee" />
        <Metric label="Total personnel cost" value={fmtMoney(calc.cost)} testid="prof-cost" />
        <Metric label="Margin $" value={`${calc.margin < 0 ? "−" : ""}${fmtMoney(Math.abs(calc.margin))}`} color={mColor} dot testid="prof-margin" />
        <Metric
          label="Margin %" headline
          value={calc.marginPct == null ? "—" : `${(calc.marginPct * 100).toFixed(1)}%`}
          sub={marginLabel}
          color={mColor} dot testid="prof-margin-pct"
        />
        <Metric
          label="Revenue / person-month"
          value={calc.revPerPm == null ? "—" : fmtMoney(calc.revPerPm)}
          sub={calc.firmRate ? `firm avg ${fmtMoney(calc.firmRate, { compact: true })}/mo` : "no rate card"}
          color={calc.revPerPm == null ? PLAIN : calc.rpmColor}
          dot={calc.revPerPm != null && !!calc.firmRate}
          testid="prof-rpm"
        />
      </div>
      <div className="px-5 pb-3 text-[11px] font-mono-num text-[var(--muted-fg)]" data-testid="prof-weighted">
        Probability-weighted · margin{" "}
        <span style={{ color: marginColor(calc.wMarginPct) }}>
          {calc.wMargin < 0 ? "−" : ""}{fmtMoney(Math.abs(calc.wMargin))}
        </span>{" "}
        ·{" "}
        <span style={{ color: marginColor(calc.wMarginPct) }}>
          {calc.wMarginPct == null ? "—" : `${(calc.wMarginPct * 100).toFixed(1)}%`}
        </span>{" "}
        — if tentative work doesn't fully land.
      </div>
    </div>
  );
}

function Metric({ label, value, sub, color = PLAIN, dot, headline, testid }) {
  return (
    <div
      className="pl-3 transition-color-smooth"
      style={{ borderLeft: `2px solid ${dot ? color : "var(--rule)"}` }}
      data-testid={testid}
    >
      <div className="eyebrow flex items-center gap-1.5">
        {dot && <span className="inline-block w-1.5 h-1.5 rounded-full transition-color-smooth" style={{ background: color }} />}
        {label}
      </div>
      <div
        className={`font-mono-num mt-0.5 transition-color-smooth ${headline ? "text-2xl font-semibold" : "text-base"}`}
        style={{ color }}
      >
        {value}
      </div>
      {sub && <div className="text-[10px] font-mono-num text-[var(--soft-fg)]">{sub}</div>}
    </div>
  );
}
